import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type {
  TrackerConnection,
  TrackerStatus,
  TrackerStatusReason,
} from "./taskTrackerApi";

// Презентационные части секции «Трекер задач» (ADR-0075), вынесенные
// из `TaskTrackerSection.tsx` ради лимита длины файла.

/** Логин принципала подключения + следствие и рекомендация (ADR-0075 § 5). */
export function PrincipalNotice(props: {
  readonly login: string;
}): ReactElement {
  const { t } = useTranslation();
  const { login } = props;

  return (
    <Stack gap={4}>
      <Text>{t("connections.taskTracker.principal.as", { login })}</Text>
      <Text size="sm" c="dimmed">
        {t("connections.taskTracker.principal.consequence")}
      </Text>
      <Text size="sm" c="dimmed">
        {t("connections.taskTracker.principal.recommendation")}
      </Text>
    </Stack>
  );
}

export interface LoginChange {
  readonly previous: string;
  readonly next: string;
}

/**
 * Предупреждения о последствиях сохранения: смена учётной записи и/или
 * смена адреса трекера/провайдера. Без того и другого рисовать нечего.
 */
export function TrackerWarnings(props: {
  readonly loginChange?: LoginChange | undefined;
  readonly targetChange: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const { loginChange, targetChange } = props;

  if (loginChange === undefined && !targetChange) {
    return null;
  }

  return (
    <Stack gap="sm">
      {loginChange === undefined ? null : (
        <Alert color="yellow">
          {t("connections.taskTracker.warning.loginChange", {
            previous: loginChange.previous,
            next: loginChange.next,
          })}
        </Alert>
      )}
      {targetChange ? (
        <Alert color="yellow">
          {t("connections.taskTracker.warning.targetChange")}
        </Alert>
      ) : null}
    </Stack>
  );
}

/** Сводка сохранённого подключения — без похода в сеть. */
export function TrackerSummary(props: {
  readonly connection: TrackerConnection;
}): ReactElement {
  const { t } = useTranslation();
  const { connection } = props;

  if (!connection.connected) {
    return <Text>{t("connections.taskTracker.summary.notConnected")}</Text>;
  }

  return (
    <Stack gap={4}>
      <Text>
        {t("connections.taskTracker.summary.provider", {
          provider: connection.provider,
        })}
      </Text>
      <Text>
        {t("connections.taskTracker.summary.host", {
          host: connection.host_url,
        })}
      </Text>
      <Text>
        {connection.principal_login === null
          ? t("connections.taskTracker.principal.unknown")
          : t("connections.taskTracker.principal.as", {
              login: connection.principal_login,
            })}
      </Text>
      {connection.has_credentials ? null : (
        <Alert color="yellow">
          {t("connections.taskTracker.summary.noCredentials")}
        </Alert>
      )}
    </Stack>
  );
}

const STATUS_REASON_KEYS: Record<TrackerStatusReason, string> = {
  host_not_allowed: "connections.taskTracker.status.reason.host_not_allowed",
  credentials_missing:
    "connections.taskTracker.status.reason.credentials_missing",
  credentials_rejected:
    "connections.taskTracker.status.reason.credentials_rejected",
  unreachable: "connections.taskTracker.status.reason.unreachable",
  provider_unknown: "connections.taskTracker.status.reason.provider_unknown",
};

/** Живой отчёт о доступности (`GET /status`) + кнопка перепроверки. */
export function TrackerStatusBlock(props: {
  readonly status: TrackerStatus;
  readonly storedLogin: string | null;
  readonly onRecheck: () => void;
  readonly isFetching: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { status, storedLogin, onRecheck, isFetching } = props;

  return (
    <Stack gap="sm">
      {status.reachable === true && status.principal_matches !== false ? (
        <Alert color="green">
          {t("connections.taskTracker.status.reachable", {
            login: status.principal_login ?? "",
          })}
        </Alert>
      ) : null}
      {status.reachable === true && status.principal_matches === false ? (
        <Alert color="yellow">
          {t("connections.taskTracker.status.mismatch", {
            actual: status.principal_login ?? "",
            stored: storedLogin ?? "",
          })}
        </Alert>
      ) : null}
      {status.reachable === false && status.reason !== null ? (
        <Alert color="red">{t(STATUS_REASON_KEYS[status.reason])}</Alert>
      ) : null}
      <Group>
        <Button variant="default" onClick={onRecheck} loading={isFetching}>
          {t("connections.taskTracker.recheckButton")}
        </Button>
      </Group>
    </Stack>
  );
}

/** Загрузка / ошибка / отчёт живого статуса подключённого трекера. */
export function TrackerLiveStatus(props: {
  readonly query: UseQueryResult<TrackerStatus, Error>;
  readonly storedLogin: string | null;
  readonly onRecheck: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { query, storedLogin, onRecheck } = props;

  if (query.isPending) {
    return <Loader size="sm" />;
  }
  if (query.isError) {
    return (
      <Stack gap="xs">
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
        <Group>
          <Button
            variant="default"
            onClick={onRecheck}
            loading={query.isFetching}
          >
            {t("connections.taskTracker.recheckButton")}
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <TrackerStatusBlock
      status={query.data}
      storedLogin={storedLogin}
      onRecheck={onRecheck}
      isFetching={query.isFetching}
    />
  );
}

export interface PendingConnect {
  readonly principalLogin: string;
  readonly loginChange?: LoginChange | undefined;
  readonly targetChange: boolean;
}

/**
 * Подтверждение «Подключить»: показывает учётную запись пробы и
 * предупреждения ДО сохранения. `pending === null` — модалка закрыта;
 * отмена и `onClose` ведут в один обработчик (не расходятся на Esc).
 */
export function ConnectConfirmModal(props: {
  readonly pending: PendingConnect | null;
  readonly isSubmitting: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { pending, isSubmitting, onConfirm, onCancel } = props;

  return (
    <Modal
      opened={pending !== null}
      onClose={onCancel}
      title={t("connections.taskTracker.confirm.title")}
      centered
    >
      {pending === null ? null : (
        <Stack gap="md">
          <PrincipalNotice login={pending.principalLogin} />
          <TrackerWarnings
            loginChange={pending.loginChange}
            targetChange={pending.targetChange}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onCancel}>
              {t("connections.modalCancel")}
            </Button>
            <Button onClick={onConfirm} loading={isSubmitting}>
              {t("connections.taskTracker.confirm.submit")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/** Подтверждение «Отключить»: статическое предупреждение, без данных пробы. */
export function DisconnectConfirmModal(props: {
  readonly opened: boolean;
  readonly isSubmitting: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { opened, isSubmitting, onConfirm, onCancel } = props;

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={t("connections.taskTracker.disconnect.title")}
      centered
    >
      <Stack gap="md">
        <Text>{t("connections.taskTracker.disconnect.text")}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            {t("connections.modalCancel")}
          </Button>
          <Button color="red" onClick={onConfirm} loading={isSubmitting}>
            {t("connections.modalConfirm")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
